import type { ListingViewerRole } from '@ralph/contracts'
import { ApiRequestError } from '@/api/client.ts'

/**
 * The rules of the agent detail sheet (PRD-22).
 *
 * A module of pure functions rather than predicates inlined in the page, for one reason that
 * this PRD exists because of: the builder used to decide `canEdit` from `ownerId` plus the
 * editor list plus the session's roles, three lines from three sources, and it got
 * administrators wrong — it showed a read-only page whose write the server accepted (F-6).
 * The role now arrives decided from the server and every question the UI asks about it is
 * answered here, once, against a test.
 *
 * Nothing in this file is authorization. `requireMakerAccess` on the server is; these
 * functions decide what to *draw*, and drawing a control somebody may not use is a defect of
 * honesty rather than of security.
 */

/**
 * How much of the sheet this reader gets.
 *
 * - `edit` — the builder, writable: the owner, a manager of the group that owns it (PRD-26 D-19),
 *   an accepted co-editor, an administrator.
 * - `read` — the builder, read-only: a co-editor who has been invited and has not accepted.
 *   They have to see what they are being asked to co-own before deciding (AGM-026), and they
 *   may change nothing until they do.
 * - `view` — the read-only sheet, at consumer fidelity: somebody the agent is shared with.
 */
export type SheetFidelity = 'edit' | 'read' | 'view'

export const sheetFidelity = (role: ListingViewerRole): SheetFidelity => {
  if (role === 'owner' || role === 'manager' || role === 'editor' || role === 'admin') return 'edit'
  if (role === 'invited') return 'read'
  return 'view'
}

/** Whether the save bar, the form controls and the preview are live. Mirrors `'edit'` access. */
export const canEditAgent = (role: ListingViewerRole): boolean => sheetFidelity(role) === 'edit'

/**
 * Whether the lifecycle bar, "Who can use it", moving it and co-editor management are live.
 *
 * Mirrors the server's `'maintain'` level: its personal owner, a manager of the group that owns
 * it (PRD-26 D-19), or an administrator — AGM-004 requires the system listing to be
 * administrable and AGM-025 requires an agent nobody maintains any more to stay manageable. A
 * co-editor does not pass, which is AGM-026 itself: they edit and publish nothing.
 */
export const canAdministerAgent = (role: ListingViewerRole): boolean =>
  role === 'owner' || role === 'manager' || role === 'admin'

/**
 * Whether the reader maintains the agent by a stake in it — its personal owner, or a manager of
 * the group that owns it — rather than by being an administrator.
 *
 * Separate from `canAdministerAgent` on purpose, because they are different sentences: one
 * decides whether to draw a button, the other whether to print "only its maintainers can…". An
 * administrator gets the buttons AND holds no stake, so a copy rule keyed on
 * `!canAdministerAgent` alone would go quiet for exactly the reader it was written for.
 */
export const ownsAgent = (role: ListingViewerRole): boolean => role === 'owner' || role === 'manager'

/**
 * Where the back link goes.
 *
 * "Your agents" is the shelf that lists what you own and what you were asked to co-edit, so it
 * is the right way up for everyone who has a stake in the agent — including an administrator,
 * whose own agents are on it and who reached this one from somewhere. A reader who is merely
 * in the audience has an empty shelf, and the gallery is where they came from.
 */
export const backDestination = (role: ListingViewerRole): '/agents' | '/agents/mine' =>
  sheetFidelity(role) === 'view' ? '/agents' : '/agents/mine'

/**
 * Which way the listing page's action commits (DTL-001).
 *
 * A discriminant rather than a string: the copy lives in the string catalogues so the
 * vocabulary lint can see it (AGM-022), and a pure module that returned prose would put half
 * of one sentence outside that catalogue.
 */
export const detailsAction = (role: ListingViewerRole): 'edit' | 'view' => (canEditAgent(role) ? 'edit' : 'view')

/** The sheet's route. One definition, so a rename is one edit rather than six. */
export const detailsHref = (agentId: string): string => `/agents/${encodeURIComponent(agentId)}/edit`

/**
 * Whether a failed read means "not yours" rather than "broken" (DTL-014).
 *
 * The server answers a hidden agent and a missing one with the same 404 (AGM-002), and the
 * sheet must answer both with the same sentence — but it must NOT answer an outage with it.
 * A page that says "this agent is not available" while the API is down sends somebody looking
 * for a permission problem that does not exist.
 */
export const isNotAvailable = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.status === 404 || error.status === 403)
